import {Component, OnInit, Inject, OnDestroy} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {FormControl} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';

import {Client} from 'src/app/client-management/models/client.interface';
import {Patient} from 'src/app/patient-management/models/patient.interface';
import {ClientService} from 'src/app/client-management/services/client.service';
import {PatientService} from 'src/app/patient-management/services/patient.service';
import {SearchService} from '../search/search-service';

export interface ClientPatientSearchData {
  selectedClient?: Client;
  selectedPatient?: Patient;
  title?: string;
}

export interface ClientPatientSearchResult {
  client: Client;
  patient: Patient;
}

@Component({
  selector: 'app-client-patient-search-dialog',
  templateUrl: './client-patient-search.dialog.html',
  styleUrls: ['./client-patient-search.dialog.scss']
})
export class ClientPatientSearchDialogComponent implements OnInit, OnDestroy {
  clientSearch = new FormControl();
  patientSearch = new FormControl('');

  selectedClient: Client;
  selectedPatient: Patient;

  filteredOptions: Observable<Client[]>;
  patientResults: Patient[] = [];
  patientOptions: Patient[] = [];

  private subscriptions = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<ClientPatientSearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClientPatientSearchData,
    private clientService: ClientService,
    private patientService: PatientService,
    private searchService: SearchService
  ) {
  }

  ngOnInit() {
    // Initialize with data if provided
    if (this.data.selectedClient) {
      this.selectedClient = this.data.selectedClient;
      this.clientSearch.setValue(this.selectedClient.name);

      if (this.data.selectedPatient) {
        this.selectedPatient = this.data.selectedPatient;
        this.patientSearch.setValue(this.selectedPatient.name);
      }
    }

    // Set up client search
    this.subscriptions.add(
      this.clientSearch.valueChanges.pipe(debounceTime(500))
        .subscribe(value => this.searchService.searchClients(value))
    );
    this.filteredOptions = this.searchService.searchClientsResultObservable;

    // Set up patient search
    this.subscriptions.add(
      this.patientSearch.valueChanges
        .pipe(debounceTime(300))
        .subscribe(data => {
          this.searchPatients(this.patientSearch.value);
        })
    );

    // Initial search with empty strings to load all options
    this.searchService.searchClients('');
    if (!this.selectedClient) {
      this.searchPatients('');
    }
  }

  selectClient(client: Client) {
    this.selectedClient = client;
    this.clientSearch.setValue(this.selectedClient.name);

    // Clear patient when client changes
    this.selectedPatient = null;
    this.patientSearch.setValue('');

    if (this.selectedClient.id) {
      this.patientResults = client.patients;
    }
  }

  searchPatients(patientSearch: string) {
    if (this.selectedClient && this.selectedClient.id !== '') {
      const newPatientList: Patient[] = [];
      this.patientResults.forEach(patient => {
        if (patient.name.toLowerCase().includes(patientSearch.toLowerCase())) {
          newPatientList.push(patient);
        }
      });
      this.patientOptions = newPatientList;
    } else if (patientSearch) {
      this.patientService.searchPatients(patientSearch).subscribe(
        response => {
          if (response.length > 0) {
            this.patientOptions = response;
          }
        }
      );
    }
  }

  onPatientSearchSelect(patient: Patient) {
    this.patientSearch.patchValue(patient.name, {emitEvent: false});
    this.selectPatient(patient);
  }

  selectPatient(patient: Patient) {
    this.selectedPatient = patient;
    this.patientSearch.setValue(this.selectedPatient.name);
  }

  confirmSelection() {
    const result: ClientPatientSearchResult = {
      client: this.selectedClient,
      patient: this.selectedPatient
    };
    this.dialogRef.close(result);
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
